package com.sweetlime.dungeonamigo.presentation.features.compendium

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.cache.Spells
import com.sweetlime.dungeonamigo.compendium.CompendiumRepository
import com.sweetlime.dungeonamigo.presentation.features.character.CharacterViewModel
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import com.sweetlime.dungeonamigo.presentation.ui.theme.silkFont
import org.koin.compose.koinInject

// characterId is passed when navigating from CharacterSheetScreen so this screen's
// own (Voyager-scoped) CharacterViewModel can load the right character.
// When null (browsing from Compendium), the linking bar shows a "no character" message.
data class SpellDetailScreen(val slug: String, val characterId: String? = null) : Screen {

    @Composable
    override fun Content() {
        val navigator  = LocalNavigator.currentOrThrow
        val repository: CompendiumRepository = koinInject()
        val characterVm: CharacterViewModel  = koinInject()
        val spell = remember(slug) { repository.getSpellBySlug(slug) }

        // Each Voyager screen gets its own ViewModel instance, so we must
        // explicitly load the character here instead of relying on shared state.
        LaunchedEffect(characterId) {
            if (characterId != null) characterVm.loadCharacter(characterId)
        }

        val selectedCharacter by characterVm.selectedCharacter.collectAsState()
        val snackbarHostState = remember { SnackbarHostState() }
        LaunchedEffect(Unit) {
            characterVm.spellError.collect { msg ->
                snackbarHostState.showSnackbar(msg, duration = SnackbarDuration.Short)
            }
        }

        Scaffold(
            containerColor = BrandColors.Vault,
            snackbarHost   = { SnackbarHost(snackbarHostState) },
        ) { innerPadding ->
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .background(BrandColors.Vault)
                    .padding(innerPadding),
            ) {
                TopBar(title = spell?.name ?: slug, onBack = { navigator.pop() })

                if (spell != null) {
                    SpellStatBlock(
                        spell    = spell,
                        modifier = Modifier.weight(1f),
                    )

                    // ── Character spell linking ──────────────────────────────────────
                    val char = selectedCharacter
                    HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.3f))
                    if (char == null) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(BrandColors.Stone)
                                .padding(12.dp),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                "Select a character to add spells",
                                color    = BrandColors.IvoryDim,
                                fontSize = 13.sp,
                            )
                        }
                    } else {
                        val isKnown    = slug in char.knownSpells
                        val isPrepared = slug in char.preparedSpells

                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(BrandColors.Stone)
                                .padding(horizontal = 16.dp, vertical = 10.dp),
                            verticalAlignment     = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            if (!isKnown) {
                                Button(
                                    onClick  = { characterVm.addSpell(slug) },
                                    colors   = ButtonDefaults.buttonColors(containerColor = BrandColors.Parchment),
                                    shape    = RoundedCornerShape(8.dp),
                                    modifier = Modifier.weight(1f),
                                ) {
                                    Text(
                                        "Add to ${char.name}'s Spells",
                                        color      = BrandColors.Vault,
                                        fontSize   = 13.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        maxLines   = 1,
                                    )
                                }
                            } else {
                                // Known chip
                                Box(
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(8.dp))
                                        .background(BrandColors.Parchment.copy(alpha = 0.2f))
                                        .padding(horizontal = 10.dp, vertical = 8.dp),
                                ) {
                                    Text("✓ Known", color = BrandColors.Parchment, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                                }

                                // Prepare / Unprepare toggle
                                OutlinedButton(
                                    onClick = { characterVm.togglePrepared(slug) },
                                    colors  = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.Parchment),
                                    shape   = RoundedCornerShape(8.dp),
                                ) {
                                    Text(
                                        if (isPrepared) "Unprepare" else "Prepare",
                                        color    = BrandColors.Parchment,
                                        fontSize = 13.sp,
                                    )
                                }

                                Spacer(Modifier.weight(1f))

                                // Remove
                                TextButton(
                                    onClick = { characterVm.removeSpell(slug) },
                                    colors  = ButtonDefaults.textButtonColors(contentColor = BrandColors.Ember),
                                ) {
                                    Text("Remove", color = BrandColors.Ember, fontSize = 13.sp)
                                }
                            }
                        }
                    }
                } else {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text("Spell not found", color = BrandColors.IvoryDim)
                    }
                }
            }
        }
    }
}

@Composable
private fun TopBar(title: String, onBack: () -> Unit) {
    val pf = pixelFont()
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(BrandColors.Stone)
            .padding(horizontal = 8.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        IconButton(onClick = onBack) {
            Icon(Icons.AutoMirrored.Filled.ArrowBack, "Back", tint = BrandColors.SlateLight)
        }
        Text(
            text       = title,
            color      = BrandColors.Ivory,
            fontFamily = pf,
            fontWeight = FontWeight.Bold,
            fontSize   = 18.sp,
        )
    }
}

@Composable
private fun SpellStatBlock(spell: Spells, modifier: Modifier = Modifier) {
    val pf = pixelFont()
    Column(
        modifier = modifier
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        // School / level / ritual tags
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment     = Alignment.CenterVertically,
        ) {
            spell.school?.let { school ->
                Surface(
                    color = BrandColors.SpellBlue.copy(alpha = 0.2f),
                    shape = RoundedCornerShape(6.dp),
                ) {
                    Text(
                        school.replaceFirstChar { it.uppercase() },
                        color    = BrandColors.SpellBlue,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                    )
                }
            }
            spell.level?.let { level ->
                val lvlInt = level.toIntOrNull()
                Text(
                    if (lvlInt == 0) "Cantrip" else "Level $level",
                    color      = BrandColors.IvoryDim,
                    fontFamily = pf,
                    fontSize   = 13.sp,
                )
            }
            if (spell.ritual?.lowercase() == "yes" || spell.ritual == "1") {
                Surface(
                    color = BrandColors.Parchment.copy(alpha = 0.2f),
                    shape = RoundedCornerShape(6.dp),
                ) {
                    Text(
                        "Ritual",
                        color    = BrandColors.Parchment,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                    )
                }
            }
        }

        // Stats card
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(12.dp))
                .background(BrandColors.Stone)
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            StatRow("Casting Time", spell.casting_time)
            StatRow("Range", spell.range)
            StatRow("Duration", spell.duration)
            spell.components?.let { components ->
                val label = if (!spell.material.isNullOrBlank()) "Components (M)" else "Components"
                StatRow(label, components)
                if (!spell.material.isNullOrBlank()) {
                    Text(
                        spell.material,
                        color    = BrandColors.IvoryDim,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(start = 4.dp),
                    )
                }
            }
            spell.classes?.let { StatRow("Classes", it) }
        }

        // Description card
        spell.description?.let { desc ->
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(BrandColors.Stone)
                    .padding(14.dp),
            ) {
                Text(desc, color = BrandColors.Ivory, fontSize = 14.sp, lineHeight = 22.sp)
            }
        }

        // At Higher Levels card
        spell.higher_level?.takeIf { it.isNotBlank() }?.let { higherLevel ->
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(BrandColors.Stone)
                    .padding(14.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    "At Higher Levels",
                    color      = BrandColors.Parchment,
                    fontFamily = pf,
                    fontWeight = FontWeight.Bold,
                    fontSize   = 14.sp,
                )
                HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.3f))
                Text(higherLevel, color = BrandColors.Ivory, fontSize = 14.sp, lineHeight = 22.sp)
            }
        }
    }
}

@Composable
private fun StatRow(label: String, value: String?) {
    if (value.isNullOrBlank()) return
    val sf = silkFont()
    Column(
        modifier            = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Text(
            text       = label.uppercase(),
            color      = BrandColors.IvoryDim,
            fontFamily = sf,
            fontSize   = 10.sp,
        )
        Text(
            text     = value,
            color    = BrandColors.Ivory,
            fontSize = 14.sp,
        )
    }
}
